import React from 'react';

const websiteIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 8h18M7 6h.01M10 6h.01" />
  </svg>
);

const softwareIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M8 9l-3 3 3 3M16 9l3 3-3 3M14 5l-4 14" />
  </svg>
);

const automationIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
    <circle cx="12" cy="12" r="5" />
    <path d="M8.5 8.5L6.4 6.4M17.6 17.6l-2.1-2.1M15.5 8.5l2.1-2.1M6.4 17.6l2.1-2.1" />
  </svg>
);

const careIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M12 3l7 3v5c0 4.8-2.9 8.1-7 10-4.1-1.9-7-5.2-7-10V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

export const services = [
  {
    id: 'website-development',
    title: 'Website Development',
    eyebrow: 'Build trust and generate leads',
    icon: websiteIcon,
    accent: '#176B8A',
    shortDesc: 'Custom business websites that make your offer clear, earn trust, and give visitors an obvious next step.',
    intro: 'We design and build fast, custom-coded websites for service businesses that need more than a pretty homepage. Every project is shaped around the questions your customers ask and the action you want them to take.',
    bestFor: 'Law firms, home services, landscaping, real estate, consultants, clinics, and local businesses',
    startingAt: 'CA$695',
    timeline: '1-5 weeks',
    features: [
      { title: 'Built around the buyer', description: 'A clear page structure, useful copy, and calls to action designed around how real customers choose.' },
      { title: 'Custom, responsive design', description: 'A distinct design system that works across phones, tablets, and desktops - not a reskinned template.' },
      { title: 'Search-ready foundation', description: 'Technical SEO, metadata, sitemap setup, performance basics, and Google Search Console submission.' },
      { title: 'A site you control', description: 'We can set up hosting and domains in your name so the core business assets remain yours.' },
    ],
    packages: [
      {
        name: 'Launch page',
        price: 'From CA$695',
        timeline: 'Usually 1 week',
        description: 'A focused launch page for one offer, using a proven structure tailored to your brand and message.',
        items: ['One responsive page', 'Contact form or booking link', 'One revision round', 'Basic metadata and launch support'],
      },
      {
        name: 'Five-page business site',
        price: 'From CA$1,995',
        timeline: 'Usually 3-5 weeks',
        description: 'A complete company presence with room to explain services, build trust, and target more than one search intent.',
        items: ['Up to five core pages', 'Conversion-focused page structure', 'Analytics and Search Console setup', 'Launch support and handoff'],
      },
      {
        name: 'Custom growth site',
        price: 'Scoped after discovery',
        timeline: 'Based on scope',
        description: 'For content-heavy, multilingual, booking, membership, or integration-driven website requirements.',
        items: ['Custom content architecture', 'Advanced integrations', 'CMS or editing workflow', 'Phased growth roadmap'],
      },
    ],
    process: [
      { title: 'Scope', description: 'We define the audience, offer, pages, content, and one primary conversion goal.' },
      { title: 'Design', description: 'You review a clear visual direction before the full site is built.' },
      { title: 'Build and test', description: 'We develop the responsive site, connect forms and analytics, then test the critical paths.' },
      { title: 'Launch and index', description: 'We connect the domain, submit the sitemap, and hand over the operating details.' },
    ],
    ongoingCosts: [
      { label: 'Domain', detail: 'Typically billed yearly by your registrar. The exact amount depends on the domain extension and registrar.' },
      { label: 'Hosting', detail: 'Simple static sites can use low-cost compatible hosting. Commercial Vercel projects may require a paid plan; we choose the right host for the project.' },
      { label: 'Third-party tools', detail: 'Booking systems, business email, premium forms, or a CMS may have separate subscriptions.' },
      { label: 'Care', detail: 'Optional maintenance starts at CA$149/month, or you can manage the site after handoff.' },
    ],
    faqs: [
      { question: 'What is the lowest-cost way to get started?', answer: 'A focused launch page starts at CA$695, before tax and third-party costs. It is best when you have one clear offer, client-supplied material, and one main action such as requesting a quote or booking a consultation.' },
      { question: 'Do you use templates?', answer: 'We build a custom visual system and page structure for the business. We may reuse proven engineering foundations where that makes the site faster and more reliable, but the customer-facing result is not a generic theme.' },
      { question: 'Is SEO included?', answer: 'We include the technical foundation: semantic pages, metadata, performance basics, a sitemap, and Search Console submission. No agency can guarantee rankings; meaningful growth also depends on competition, content, reviews, and ongoing authority.' },
      { question: 'Who owns the domain and hosting?', answer: 'Our preferred setup puts the domain and hosting account in your name. We can configure and manage them for you while keeping ownership clear.' },
    ],
  },
  {
    id: 'custom-software',
    title: 'Custom Software',
    eyebrow: 'Replace scattered work with one system',
    icon: softwareIcon,
    accent: '#885C26',
    shortDesc: 'Purpose-built portals, internal tools, APIs, and customer platforms shaped around the way your business actually works.',
    intro: 'When spreadsheets, inboxes, and disconnected subscriptions start getting in the way, we turn the workflow into a maintainable software system. We begin with the smallest useful release and design for what needs to scale next.',
    bestFor: 'Operations teams, SaaS founders, data products, service portals, and workflow-heavy businesses',
    startingAt: 'CA$5,000',
    timeline: '4-12+ weeks',
    features: [
      { title: 'Workflow-first planning', description: 'We map users, steps, permissions, and exceptions before choosing technology.' },
      { title: 'Portals and internal tools', description: 'Dashboards, client portals, admin systems, quoting tools, and operational software.' },
      { title: 'APIs and integrations', description: 'Connect the tools you already rely on and remove repetitive handoffs between them.' },
      { title: 'A scalable foundation', description: 'Sensible data models, security boundaries, monitoring, and a roadmap for the next phase.' },
    ],
    packages: [
      {
        name: 'Paid discovery',
        price: 'From CA$750',
        timeline: 'Usually 1 week',
        description: 'Turn an early idea or tangled workflow into a buildable scope, technical direction, and phased estimate.',
        items: ['Workflow mapping', 'Feature priorities', 'Technical recommendation', 'Build roadmap and estimate'],
      },
      {
        name: 'Focused first release',
        price: 'From CA$5,000',
        timeline: 'Usually 4-8 weeks',
        description: 'The smallest complete product that solves the central problem and is ready for real users.',
        items: ['Custom interface and application', 'Database and authentication', 'Core integrations', 'Deployment and handoff'],
      },
      {
        name: 'Growth system',
        price: 'Scoped in phases',
        timeline: 'Based on roadmap',
        description: 'A larger platform delivered in controlled stages, with ongoing releases and technical ownership.',
        items: ['Multi-role workflows', 'Advanced reporting', 'Performance and reliability work', 'Ongoing product development'],
      },
    ],
    process: [
      { title: 'Map the work', description: 'We document what happens now, where it breaks, and what success looks like.' },
      { title: 'Choose the first release', description: 'We prioritize the smallest useful system instead of funding every possible feature at once.' },
      { title: 'Build in visible stages', description: 'You review working software throughout the project, not only at the end.' },
      { title: 'Launch and improve', description: 'We monitor the release, collect feedback, and plan the next highest-value improvements.' },
    ],
    ongoingCosts: [
      { label: 'Infrastructure', detail: 'Database, hosting, storage, email, and monitoring costs depend on traffic and usage.' },
      { label: 'Third-party APIs', detail: 'Payments, mapping, messaging, data providers, and other integrations may charge per month or per request.' },
      { label: 'Support', detail: 'Ongoing support can be handled through a monthly retainer or scoped releases.' },
      { label: 'Scaling', detail: 'We review cost and architecture as usage grows instead of overbuilding on day one.' },
    ],
    faqs: [
      { question: 'Why does custom software need discovery?', answer: 'The expensive mistakes usually happen before development: unclear users, missing edge cases, and an oversized first release. Discovery gives both sides a concrete scope and reduces that risk.' },
      { question: 'Can you improve an existing system?', answer: 'Yes. We can assess an existing application, stabilize high-risk areas, add integrations, or plan a staged replacement where a full rewrite would be wasteful.' },
      { question: 'What will it cost to operate?', answer: 'That depends on users, data, external APIs, and reliability needs. We identify the expected recurring services during scoping and keep them visible in the proposal.' },
    ],
  },
  {
    id: 'ai-automation',
    title: 'AI Automation',
    eyebrow: 'Automate the repetitive parts of work',
    icon: automationIcon,
    accent: '#705A8C',
    shortDesc: 'Practical AI workflows, assistants, and document systems that save your team from repetitive work without losing oversight.',
    intro: 'We use tools such as Claude and OpenAI models where they are genuinely useful: understanding documents, drafting structured responses, routing information, and answering from approved knowledge. Every workflow keeps a clear place for human review.',
    bestFor: 'Document-heavy teams, support workflows, lead qualification, internal knowledge, and recurring admin work',
    startingAt: 'CA$1,500',
    timeline: '2-6 weeks',
    features: [
      { title: 'Workflow automation', description: 'Connect forms, inboxes, documents, CRMs, and internal tools around one reliable process.' },
      { title: 'Knowledge assistants', description: 'Answer from your approved material with sources, access rules, and clear escalation paths.' },
      { title: 'Document processing', description: 'Extract, categorize, summarize, and route information while preserving review checkpoints.' },
      { title: 'Measured rollout', description: 'Test a narrow workflow first, monitor the result, and expand only when the value is clear.' },
    ],
    packages: [
      {
        name: 'Workflow pilot',
        price: 'From CA$1,500',
        timeline: 'Usually 2-3 weeks',
        description: 'Prove one high-friction workflow with a bounded pilot and a clear before-and-after measure.',
        items: ['Workflow review', 'One focused automation', 'Human review step', 'Documentation and handoff'],
      },
      {
        name: 'Connected AI system',
        price: 'Scoped after discovery',
        timeline: 'Usually 4-8 weeks',
        description: 'A secure workflow that connects AI to your business data and the tools your team already uses.',
        items: ['Custom interface or chatbot', 'Approved knowledge sources', 'Business-tool integrations', 'Logging and safeguards'],
      },
      {
        name: 'Ongoing optimization',
        price: 'Monthly retainer',
        timeline: 'Ongoing',
        description: 'Monitoring, prompt and workflow improvements, model changes, and new automations as the business learns.',
        items: ['Usage and quality review', 'Model and prompt updates', 'Workflow improvements', 'Priority support'],
      },
    ],
    process: [
      { title: 'Find the right task', description: 'We look for repetitive, frequent work with clear inputs, outputs, and review rules.' },
      { title: 'Set the guardrails', description: 'We define approved data, failure cases, privacy needs, and when a person must take over.' },
      { title: 'Pilot with real examples', description: 'The workflow is tested against representative work before it reaches the wider team.' },
      { title: 'Measure and expand', description: 'We review quality, cost, and time saved, then decide whether the next automation is worthwhile.' },
    ],
    ongoingCosts: [
      { label: 'Model usage', detail: 'Claude, OpenAI, or another model provider normally charges based on usage. We estimate this before launch and expose the cost.' },
      { label: 'Automation tools', detail: 'Workflow platforms, vector databases, or messaging services may have their own subscriptions.' },
      { label: 'Monitoring', detail: 'Production automations need logs and periodic review as business rules and models change.' },
      { label: 'Maintenance', detail: 'Support can be included in a retainer or handled as scheduled improvement work.' },
    ],
    faqs: [
      { question: 'Will AI replace my team?', answer: 'The goal is to remove repetitive handling and give people better starting points. Important decisions, sensitive communication, and exceptions should still have human oversight.' },
      { question: 'Which AI model do you use?', answer: 'We choose based on the job, privacy needs, quality, latency, and cost. That can include Claude, OpenAI models, or a non-AI rule-based step when it is more reliable.' },
      { question: 'Are usage costs included?', answer: 'Implementation and provider usage are separate. We estimate the likely model and platform costs during scoping, and whenever possible the accounts are owned directly by you.' },
    ],
  },
  {
    id: 'maintenance-support',
    title: 'Maintenance & Support',
    eyebrow: 'Keep the system healthy after launch',
    icon: careIcon,
    accent: '#337B63',
    shortDesc: 'A practical care plan for updates, monitoring, small improvements, and someone to call when the business changes.',
    intro: 'Launch is a milestone, not the end of ownership. Our retainers keep your website or application current and give you a dependable route for fixes and small improvements.',
    bestFor: 'Businesses that want a technical partner after launch',
    startingAt: 'CA$149/month',
    timeline: 'Month to month',
    features: [
      { title: 'Routine care', description: 'Dependency updates, backups, uptime checks, and basic security maintenance where applicable.' },
      { title: 'A support channel', description: 'A clear place to report an issue or ask for a scoped change.' },
      { title: 'Small improvements', description: 'Use included time or a monthly budget for content, conversion, and workflow refinements.' },
      { title: 'Visible priorities', description: 'Know what is being handled now, what is planned, and what needs separate approval.' },
    ],
    packages: [
      {
        name: 'Website care',
        price: 'From CA$149/month',
        timeline: 'Month to month',
        description: 'Essential monitoring and maintenance for a brochure or lead-generation website.',
        items: ['Uptime monitoring', 'Routine updates', 'Form checks', 'Monthly support allowance'],
      },
      {
        name: 'Growth care',
        price: 'From CA$349/month',
        timeline: 'Month to month',
        description: 'For businesses that want regular website changes, reporting, and conversion improvements.',
        items: ['Everything in Website care', 'Content and layout updates', 'Analytics review', 'Priority response'],
      },
      {
        name: 'Software support',
        price: 'Custom retainer',
        timeline: 'Ongoing',
        description: 'Application monitoring, fixes, infrastructure oversight, and planned releases.',
        items: ['Application monitoring', 'Issue triage', 'Release management', 'Planned engineering capacity'],
      },
    ],
    process: [
      { title: 'Baseline', description: 'We review the current system, ownership, dependencies, and known risks.' },
      { title: 'Agree on coverage', description: 'The retainer defines response expectations, included work, and what requires a new scope.' },
      { title: 'Maintain', description: 'We monitor, update, and resolve issues within the agreed service level.' },
      { title: 'Improve', description: 'Regular reviews turn recurring requests into a prioritized improvement plan.' },
    ],
    ongoingCosts: [
      { label: 'Monthly retainer', detail: 'The selected support plan covers the agreed maintenance and support allowance.' },
      { label: 'Hosting and tools', detail: 'Infrastructure and third-party subscriptions remain separate and visible.' },
      { label: 'Larger changes', detail: 'New features or redesigns are estimated before work begins rather than quietly consuming maintenance time.' },
    ],
    faqs: [
      { question: 'Is maintenance required?', answer: 'No. We can hand over the project with documentation. A care plan is useful when you want someone else to monitor the details and respond as the business changes.' },
      { question: 'What counts as a small improvement?', answer: 'Examples include copy changes, replacing images, adjusting forms, small layout updates, and minor configuration work. New pages, integrations, or features are scoped separately.' },
    ],
  },
];

export const coreServices = services.filter((service) => service.id !== 'maintenance-support');

export const getServiceById = (id) => services.find((service) => service.id === id);

export default services;
